import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, Minus, RotateCcw, Share2, Monitor, LogOut } from 'lucide-react';
import initiationAudio from './src/audio_file/Initiation.mp3';
import audio70 from './src/audio_file/70.mp3';
import chartConnectedAudio from './src/audio_file/chart connected.mp3';
import chartDisconnectedAudio from './src/audio_file/chart disconnected.mp3';
import backgroundAmbienceAudio from './src/audio_file/background_ambience.wav';
import scanInProgressAudio from './src/audio_file/scan_Inprogress.mp3';
import sessionCloseAudio from './src/audio_file/session close message.mp3';
import mode1Audio from './src/audio_file/mode1.mp3';
import mode2Audio from './src/audio_file/mode2.mp3';
import mode3Audio from './src/audio_file/mode3.mp3';
import powerOffAudio from './src/audio_file/power off.mp3';
import { account } from './src/lib/appwrite';
import { createSession, endSession } from './src/services/appwriteDatabase';
// Background video served from public directory
const backgroundVideo = '/Background.mp4';

// Type declaration for NodeJS timeout
declare global {
  namespace NodeJS {
    interface Timeout {}
  }
}

// TypeScript interfaces for better type safety
interface Candle {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  isBullish: boolean;
}

interface TradingSignal {
  type: 'BUY' | 'SELL' | 'CONSOLIDATION';
  pattern: string;
  confidence: number;
  time: string;
  price: number;
  expiry: number;
  isConsolidation?: boolean;
}

interface MarketStats {
  green: number;
  red: number;
  consolidation: boolean;
}

interface ConsolidationAnalysis {
  consolidation: boolean;
  green: number;
  red: number;
  suggestion?: 'BUY' | 'SELL' | null;
}

interface ChartScale {
  min: number;
  max: number;
}

interface TradeRecord {
  id: string;
  signal: TradingSignal;
  entryTime: Date;
  entryPrice: number;
  expiryTime: Date;
  exitPrice?: number;
  result?: 'WIN' | 'LOSS' | 'PENDING';
  profit?: number;
}

interface PerformanceStats {
  totalTrades: number;
  winRate: number;
  totalProfit: number;
  avgProfit: number;
  bestTrade: number;
  worstTrade: number;
  winStreak: number;
  lossStreak: number;
  currentStreak: number;
  currentStreakType: 'WIN' | 'LOSS' | 'PENDING' | 'NONE';
}

interface TechnicalIndicators {
  rsi: number;
  macd: {
    macd: number;
    signal: number;
    histogram: number;
  };
  sma20: number;
  ema5: number;
  ema13: number;
  ema62: number;
}

type DisplayMode = 'CHART' | 'HISTORY' | 'ANALYTICS' | 'COMPACT';

const BinaryTradingBot: React.FC = () => {
  // LocalStorage keys
  const STORAGE_KEYS = {
    TRADE_HISTORY: 'binaryBot_tradeHistory',
    PERFORMANCE_STATS: 'binaryBot_performanceStats',
    BOT_SETTINGS: 'binaryBot_settings'
  };

  // Data persistence functions
  const saveToLocalStorage = (key: string, data: any) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  };

  const loadFromLocalStorage = (key: string, defaultValue: any) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
      return defaultValue;
    }
  };

  const [candles, setCandles] = useState<Candle[]>([]);
  const [signal, setSignal] = useState<TradingSignal | null>(null);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [timeframe, setTimeframe] = useState<number>(15);
  const [tradeExpiry, setTradeExpiry] = useState<number>(7);
  const [chartMode, setChartMode] = useState<'candlestick' | 'line'>('candlestick');
  const [marketStats, setMarketStats] = useState<MarketStats>({ green: 0, red: 0, consolidation: false });
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [systemError, setSystemError] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => 
    loadFromLocalStorage(STORAGE_KEYS.BOT_SETTINGS, { soundEnabled: true }).soundEnabled
  );
  const [minConfidence, setMinConfidence] = useState<number>(() =>
    loadFromLocalStorage(STORAGE_KEYS.BOT_SETTINGS, { minConfidence: 60 }).minConfidence ?? 60
  );
  const [tradeHistory, setTradeHistory] = useState<TradeRecord[]>(() => {
    const storedHistory = loadFromLocalStorage(STORAGE_KEYS.TRADE_HISTORY, []);
    // Convert string dates back to Date objects
    return storedHistory.map((trade: any) => ({
      ...trade,
      entryTime: new Date(trade.entryTime),
      expiryTime: new Date(trade.expiryTime)
    }));
  });
  const [performanceStats, setPerformanceStats] = useState<PerformanceStats>(() => 
    loadFromLocalStorage(STORAGE_KEYS.PERFORMANCE_STATS, {
      totalTrades: 0,
      winRate: 0,
      totalProfit: 0,
      avgProfit: 0,
      bestTrade: 0,
      worstTrade: 0,
      winStreak: 0,
      lossStreak: 0,
      currentStreak: 0,
      currentStreakType: 'NONE'
    })
  );
  const [displayMode, setDisplayMode] = useState<DisplayMode>('CHART');
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [technicalIndicators, setTechnicalIndicators] = useState<TechnicalIndicators>({
    rsi: 50,
    macd: { macd: 0, signal: 0, histogram: 0 },
    sma20: 0,
    ema5: 0,
    ema13: 0,
    ema62: 0
  });
  const [macdHistory, setMacdHistory] = useState<number[]>([]);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [timerActive, setTimerActive] = useState<boolean>(false);
  const [sessionDuration, setSessionDuration] = useState<number>(15 * 60); // session duration in seconds
  const [timeRemaining, setTimeRemaining] = useState<number>(15 * 60); // 15 minutes in seconds
  const intervalRef = useRef<number | null>(null);
  const chartRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const backgroundAmbienceRef = useRef<HTMLAudioElement | null>(null);
  const [sessionDocId, setSessionDocId] = useState<string | null>(null);
  const [sessionMode, setSessionMode] = useState<'MODE_1' | 'MODE_2' | 'MODE_3'>('MODE_1');
  const maxCandles: number = 20;

  // Sound notification functions
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (error) {
        console.warn('Audio context not supported:', error);
      }
    }
  };

  const playSignalSound = (signalType: 'BUY' | 'SELL' | 'CONSOLIDATION') => {
    if (!soundEnabled || !audioContextRef.current) return;

    try {
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      // Different frequencies for different signal types
      if (signalType === 'BUY') {
        // Higher pitch for buy signals (more optimistic)
        oscillator.frequency.setValueAtTime(800, ctx.currentTime);
        oscillator.frequency.setValueAtTime(1000, ctx.currentTime + 0.1);
      } else if (signalType === 'SELL') {
        // Lower pitch for sell signals
        oscillator.frequency.setValueAtTime(400, ctx.currentTime);
        oscillator.frequency.setValueAtTime(300, ctx.currentTime + 0.1);
      } else {
        // Medium pitch for consolidation signals (neutral)
        oscillator.frequency.setValueAtTime(600, ctx.currentTime);
        oscillator.frequency.setValueAtTime(600, ctx.currentTime + 0.1);
      }

      oscillator.type = 'sine';
      
      // Volume envelope
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (error) {
      console.warn('Error playing sound:', error);
    }
  };

  // Function to play initiation audio when bot starts
  const playInitiationSound = () => {
    try {
      const audio = new Audio(initiationAudio);
      audio.volume = 0.3; // Set volume to 30%
      audio.play().catch(error => {
        console.warn('Error playing initiation sound:', error);
      });
    } catch (error) {
      console.warn('Error creating initiation audio:', error);
    }
  };

  // Function to play confidence-based audio
  const playConfidenceAudio = (confidence: number) => {
    if (!soundEnabled) return;
    
    try {
      // Play 70% audio when confidence is exactly 70%
      if (confidence === 70) {
        const audio = new Audio(audio70);
        audio.volume = 0.4; // Set volume to 40%
        audio.play().catch(error => {
          console.warn('Error playing 70% confidence sound:', error);
        });
      }
    } catch (error) {
      console.warn('Error creating confidence audio:', error);
    }
  };

  // Function to play chart connected audio when screen sharing starts
  const playChartConnectedAudio = () => {
    if (!soundEnabled) return;
    
    try {
      const audio = new Audio(chartConnectedAudio);
      audio.volume = 0.3; // Set volume to 30%
      audio.play().catch(error => {
        console.warn('Error playing chart connected sound:', error);
      });
    } catch (error) {
      console.warn('Error creating chart connected audio:', error);
    }
  };

  // Function to play chart disconnected audio when screen sharing stops
  const playChartDisconnectedAudio = () => {
    if (!soundEnabled) return;
    
    try {
      const audio = new Audio(chartDisconnectedAudio);
      audio.volume = 0.4; // Set volume to 40%
      audio.play().catch(error => {
        console.warn('Error playing chart disconnected sound:', error);
      });
    } catch (error) {
      console.warn('Error creating chart disconnected audio:', error);
    }
  };

  // Background ambience audio functions
  const playBackgroundAmbience = () => {
    if (!soundEnabled) return;
    
    try {
      // Stop any existing background ambience
      stopBackgroundAmbience();
      
      const audio = new Audio(backgroundAmbienceAudio);
      audio.volume = 0.07; // Set volume to 7% for ambient sound
      audio.loop = true; // Enable looping
      backgroundAmbienceRef.current = audio;
      
      audio.play().catch(error => {
        console.warn('Error playing background ambience:', error);
      });
    } catch (error) {
      console.warn('Error creating background ambience audio:', error);
    }
  };

  const stopBackgroundAmbience = () => {
    if (backgroundAmbienceRef.current) {
      backgroundAmbienceRef.current.pause();
      backgroundAmbienceRef.current.currentTime = 0;
      backgroundAmbienceRef.current = null;
    }
  };

  // Scan in progress audio function
  const playScanInProgressAudio = () => {
    try {
      const audio = new Audio(scanInProgressAudio);
      audio.volume = 0.4; // Set volume to 40%
      audio.play().catch(error => {
        console.warn('Error playing scan in progress audio:', error);
      });
    } catch (error) {
      console.warn('Error creating scan in progress audio:', error);
    }
  };

  // Session close audio function
  const playSessionCloseAudio = () => {
    if (!soundEnabled) return;
    
    try {
      const audio = new Audio(sessionCloseAudio);
      audio.volume = 0.4; // Set volume to 40%
      audio.play().catch(error => {
        console.warn('Error playing session close audio:', error);
      });
    } catch (error) {
      console.warn('Error creating session close audio:', error);
    }
  };

  // Mode 1 audio function
  const playMode1Audio = () => {
    if (!soundEnabled) return;
    
    try {
      const audio = new Audio(mode1Audio);
      audio.volume = 0.3; // Set volume to 30%
      audio.play().catch(error => {
        console.warn('Error playing mode 1 audio:', error);
      });
    } catch (error) {
      console.warn('Error creating mode 1 audio:', error);
    }
  };

  // Mode 2 audio function
  const playMode2Audio = () => {
    if (!soundEnabled) return;
    
    try {
      const audio = new Audio(mode2Audio);
      audio.volume = 0.3; // Set volume to 30%
      audio.play().catch(error => {
        console.warn('Error playing mode 2 audio:', error);
      });
    } catch (error) {
      console.warn('Error creating mode 2 audio:', error);
    }
  };

  // Mode 3 audio function
  const playMode3Audio = () => {
    if (!soundEnabled) return;
    
    try {
      const audio = new Audio(mode3Audio);
      audio.volume = 0.3; // Set volume to 30%
      audio.play().catch(error => {
        console.warn('Error playing mode 3 audio:', error);
      });
    } catch (error) {
      console.warn('Error creating mode 3 audio:', error);
    }
  };

  // Power off audio function
  const playPowerOffAudio = () => {
    if (!soundEnabled) return;
    
    try {
      const audio = new Audio(powerOffAudio);
      audio.volume = 0.3; // Set volume to 30% for power off
      audio.play().catch(error => {
        console.warn('Error playing power off audio:', error);
      });
    } catch (error) {
      console.warn('Error creating power off audio:', error);
    }
  };

  // Session end sequence function
  const handleSessionEnd = async () => {
    try {
      // 1. Play session close audio
      playSessionCloseAudio();
      
      // 2. Wait for session close audio to finish (approximately 3 seconds)
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // 3. Disconnect chart if connected
      if (isSharing) {
        stopScreenShare();
        
        // 4. Wait for chart disconnect audio to finish (approximately 2 seconds)
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      // 5. Reset the bot to inactive state
      if (sessionDocId) {
        try {
          await endSession(sessionDocId, new Date().toISOString());
        } catch (e) {
          console.error('Failed to end session document:', e);
        } finally {
          setSessionDocId(null);
        }
      }
      reset();
    } catch (error) {
      console.error('Error in session end sequence:', error);
      // Fallback: just reset the bot
      reset();
    }
  };

  // Trade tracking functions
  const recordTrade = (signal: TradingSignal) => {
    const trade: TradeRecord = {
      id: `trade_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      signal,
      entryTime: new Date(),
      entryPrice: signal.price,
      expiryTime: new Date(Date.now() + signal.expiry * 1000),
      result: 'PENDING'
    };
    
    const updatedHistory = [trade, ...tradeHistory].slice(0, 100); // Keep last 100 trades
    setTradeHistory(updatedHistory);
    saveToLocalStorage(STORAGE_KEYS.TRADE_HISTORY, updatedHistory);
    return trade;
  };

  const updateTradeResult = (tradeId: string, exitPrice: number) => {
    setTradeHistory(prev => {
      const updatedHistory = prev.map(trade => {
        if (trade.id === tradeId && trade.result === 'PENDING') {
          // CONSOLIDATION signals are informational only, don't evaluate as win/loss
          if (trade.signal.type === 'CONSOLIDATION') {
            return {
              ...trade,
              exitPrice,
              result: 'WIN' as 'WIN' | 'LOSS', // Mark as neutral/informational
              profit: 0 // No profit/loss for consolidation signals
            };
          }
          
          const isWin = (trade.signal.type === 'BUY' && exitPrice > trade.entryPrice) ||
                       (trade.signal.type === 'SELL' && exitPrice < trade.entryPrice);
          
          const scale = Math.max(0, Math.min(1, (trade.signal.confidence || 0) / 100));
          const profit = isWin ? 0.8 * scale : -1 * scale; // Payout scaled by confidence
          
          return {
            ...trade,
            exitPrice,
            result: isWin ? 'WIN' : 'LOSS' as 'WIN' | 'LOSS',
            profit
          };
        }
        return trade;
      });
      
      // Update performance stats
      calculatePerformanceStats(updatedHistory);
      saveToLocalStorage(STORAGE_KEYS.TRADE_HISTORY, updatedHistory);
      return updatedHistory;
    });
  };

  const calculatePerformanceStats = (history: TradeRecord[]) => {
    const completedTrades = history.filter(trade => trade.result !== 'PENDING');
    
    if (completedTrades.length === 0) {
      const emptyStats = {
        totalTrades: 0,
        winRate: 0,
        totalProfit: 0,
        avgProfit: 0,
        bestTrade: 0,
        worstTrade: 0,
        winStreak: 0,
        lossStreak: 0,
        currentStreak: 0,
        currentStreakType: 'NONE' as 'WIN' | 'LOSS' | 'NONE'
      };
      setPerformanceStats(emptyStats);
      saveToLocalStorage(STORAGE_KEYS.PERFORMANCE_STATS, emptyStats);
      return;
    }

    const wins = completedTrades.filter(trade => trade.result === 'WIN');
    const profits = completedTrades.map(trade => trade.profit || 0);
    
    const totalProfit = profits.reduce((sum, profit) => sum + profit, 0);
    const winRate = (wins.length / completedTrades.length) * 100;
    
    // Calculate streaks
    let currentStreak = 0;
    let currentStreakType: 'WIN' | 'LOSS' | 'PENDING' | 'NONE' = 'NONE';
    let maxWinStreak = 0;
    let maxLossStreak = 0;
    let tempWinStreak = 0;
    let tempLossStreak = 0;

    for (let i = 0; i < completedTrades.length; i++) {
      const trade = completedTrades[i];
      
      if (i === 0) {
        currentStreakType = trade.result!;
        currentStreak = 1;
      } else if (trade.result === currentStreakType) {
        currentStreak++;
      } else {
        currentStreakType = trade.result!;
        currentStreak = 1;
      }

      if (trade.result === 'WIN') {
        tempWinStreak++;
        tempLossStreak = 0;
        maxWinStreak = Math.max(maxWinStreak, tempWinStreak);
      } else {
        tempLossStreak++;
        tempWinStreak = 0;
        maxLossStreak = Math.max(maxLossStreak, tempLossStreak);
      }
    }

    const newStats = {
      totalTrades: completedTrades.length,
      winRate,
      totalProfit,
      avgProfit: totalProfit / completedTrades.length,
      bestTrade: Math.max(...profits),
      worstTrade: Math.min(...profits),
      winStreak: maxWinStreak,
      lossStreak: maxLossStreak,
      currentStreak,
      currentStreakType
    };
    
    setPerformanceStats(newStats);
    saveToLocalStorage(STORAGE_KEYS.PERFORMANCE_STATS, newStats);
  };

  // Technical Indicator Calculations
  const calculateRSI = (prices: number[], period: number = 17): number => {
    // Adaptive RSI: use available history so RSI moves as soon as there are at least 2 prices
    if (prices.length < 2) return 50;

    const n = Math.min(period, prices.length - 1);
    let gains = 0;
    let losses = 0;

    for (let i = 1; i <= n; i++) {
      const change = prices[prices.length - i] - prices[prices.length - i - 1];
      if (change > 0) gains += change;
      else losses -= change; // accumulate absolute loss
    }

    const avgGain = gains / n;
    const avgLoss = losses / n;

    if (avgLoss === 0) {
      // If there are no losses, RSI should be 100 unless there are also no gains
      return avgGain === 0 ? 50 : 100;
    }

    const rs = avgGain / avgLoss;
    return 100 - (100 / (1 + rs));
  };

  const calculateSMA = (prices: number[], period: number): number => {
    if (prices.length < period) return prices[prices.length - 1] || 0;
    
    const sum = prices.slice(-period).reduce((acc, price) => acc + price, 0);
    return sum / period;
  };

  const calculateEMA = (prices: number[], period: number): number => {
    if (prices.length < period) return prices[prices.length - 1] || 0;
    
    const multiplier = 2 / (period + 1);
    let ema = prices.slice(0, period).reduce((acc, price) => acc + price, 0) / period;
    
    for (let i = period; i < prices.length; i++) {
      ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
    }
    
    return ema;
  };

  const calculateMACD = (prices: number[], macdHistoryValues: number[]): { macd: number; signal: number; histogram: number; newMacdHistory: number[] } => {
    if (prices.length < 20) return { macd: 0, signal: 0, histogram: 0, newMacdHistory: macdHistoryValues };
    
    // MACD with fast period 9, slow period 20
    const emaFast = calculateEMA(prices, 9);
    const emaSlow = calculateEMA(prices, 20);
    const macd = emaFast - emaSlow;
    
    // Update MACD history for signal line calculation
    const newMacdHistory = [...macdHistoryValues, macd];
    // Keep only the last 10 values for efficiency
    if (newMacdHistory.length > 10) {
      newMacdHistory.shift();
    }
    
    // Signal line is EMA of MACD values with period 3
    let signal = 0;
    if (newMacdHistory.length >= 3) {
      signal = calculateEMA(newMacdHistory, 3);
    } else {
      // Use simple average if we don't have enough history
      signal = newMacdHistory.reduce((sum, val) => sum + val, 0) / newMacdHistory.length;
    }
    
    const histogram = macd - signal;
    
    return { macd, signal, histogram, newMacdHistory };
  };

  const updateTechnicalIndicators = (candleData: Candle[]) => {
    if (candleData.length < 2) return;
    
    const closePrices = candleData.map(c => c.close);
    
    const rsi = calculateRSI(closePrices);
    const macdResult = calculateMACD(closePrices, macdHistory);
    const sma20 = calculateSMA(closePrices, 20);
    const ema5 = calculateEMA(closePrices, 5);
    const ema13 = calculateEMA(closePrices, 13);
    const ema62 = calculateEMA(closePrices, 62);
    
    // Update MACD history state
    setMacdHistory(macdResult.newMacdHistory);
    
    setTechnicalIndicators({
      rsi,
      macd: {
        macd: macdResult.macd,
        signal: macdResult.signal,
        histogram: macdResult.histogram
      },
      sma20,
      ema5,
      ema13,
      ema62
    });
  };

  // RSI consolidation zone helpers
  const RSI_CONSOLIDATION_LOW = 47;
  const RSI_CONSOLIDATION_HIGH = 53;
  const isRSIConsolidation = (rsi: number) => rsi >= RSI_CONSOLIDATION_LOW && rsi <= RSI_CONSOLIDATION_HIGH;
  const isRSITradeable = (rsi: number) => rsi < RSI_CONSOLIDATION_LOW || rsi > RSI_CONSOLIDATION_HIGH;

  // Confidence color helper
  const getConfidenceColor = (value: number) => {
    if (value >= 85) return 'text-green-400';
    if (value >= 70) return 'text-yellow-300';
    return 'text-red-400';
  };

  // Validation functions
  const validateCandle = (candle: Candle): boolean => {
    return (
      typeof candle.open === 'number' &&
      typeof candle.high === 'number' &&
      typeof candle.low === 'number' &&
      typeof candle.close === 'number' &&
      candle.high >= Math.max(candle.open, candle.close) &&
      candle.low <= Math.min(candle.open, candle.close) &&
      candle.open > 0 &&
      candle.high > 0 &&
      candle.low > 0 &&
      candle.close > 0
    );
  };

  const validateSignal = (signal: TradingSignal): boolean => {
    return (
      (signal.type === 'BUY' || signal.type === 'SELL' || signal.type === 'CONSOLIDATION') &&
      typeof signal.confidence === 'number' &&
      signal.confidence >= 0 &&
      signal.confidence <= 100 &&
      typeof signal.price === 'number' &&
      signal.price > 0 &&
      typeof signal.expiry === 'number' &&
      signal.expiry > 0
    );
  };

  // Error handling wrapper
  const withErrorHandling = <T extends any[], R>(
    fn: (...args: T) => R,
    errorMessage: string
  ) => {
    return (...args: T): R | null => {
      try {
        setSystemError(null);
        return fn(...args);
      } catch (error) {
        console.error(`${errorMessage}:`, error);
        setSystemError(`${errorMessage}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return null;
      }
    };
  };

  // Generate realistic candle data
  const generateCandle = (prevClose?: number | null): Candle => {
    const volatility = 0.002;
    const trend = Math.random() - 0.48; // Slight bullish bias
    
    const open = prevClose || 100 + Math.random() * 10;
    const change = open * volatility * (Math.random() * 2 - 1 + trend);
    const close = open + change;
    
    const high = Math.max(open, close) + Math.abs(change) * Math.random();
    const low = Math.min(open, close) - Math.abs(change) * Math.random();
    
    return {
      time: new Date().toLocaleTimeString(),
      open: parseFloat(open.toFixed(2)),
      high: parseFloat(high.toFixed(2)),
      low: parseFloat(low.toFixed(2)),
      close: parseFloat(close.toFixed(2)),
      isBullish: close > open
    };
  };

  // Analyze candlestick patterns
  const analyzePattern = (candleData: Candle[]): TradingSignal | null => {
    if (candleData.length < 3) return null;

    const recent = candleData.slice(-3);
    const [c1, c2, c3] = recent;
    
    // Pattern recognition
    const body3 = Math.abs(c3.close - c3.open);
    
    const upperShadow3 = c3.high - Math.max(c3.open, c3.close);
    const lowerShadow3 = Math.min(c3.open, c3.close) - c3.low;
    
    // Bullish Engulfing
    if (!c1.isBullish && c2.isBullish && c2.close > c1.open && c2.open < c1.close) {
      return { type: 'BUY', pattern: 'Bullish Engulfing', confidence: 85, time: c3.time, price: c3.close, expiry: 60 };
    }
    
    // Bearish Engulfing
    if (c1.isBullish && !c2.isBullish && c2.close < c1.open && c2.open > c1.close) {
      return { type: 'SELL', pattern: 'Bearish Engulfing', confidence: 85, time: c3.time, price: c3.close, expiry: 60 };
    }
    
    // Hammer (bullish reversal)
    if (lowerShadow3 > body3 * 2 && upperShadow3 < body3 * 0.3 && !c1.isBullish) {
      return { type: 'BUY', pattern: 'Hammer', confidence: 75, time: c3.time, price: c3.close, expiry: 60 };
    }
    
    // Shooting Star (bearish reversal)
    if (upperShadow3 > body3 * 2 && lowerShadow3 < body3 * 0.3 && c1.isBullish) {
      return { type: 'SELL', pattern: 'Shooting Star', confidence: 75, time: c3.time, price: c3.close, expiry: 60 };
    }
    
    // Three consecutive bullish candles
    if (c1.isBullish && c2.isBullish && c3.isBullish) {
      return { type: 'BUY', pattern: 'Bullish Trend', confidence: 70, time: c3.time, price: c3.close, expiry: 60 };
    }
    
    // Three consecutive bearish candles
    if (!c1.isBullish && !c2.isBullish && !c3.isBullish) {
      return { type: 'SELL', pattern: 'Bearish Trend', confidence: 70, time: c3.time, price: c3.close, expiry: 60 };
    }
    
    return null;
  };
  
  // Analyze line momentum/trend
  const analyzeLine = (candleData: Candle[]): TradingSignal | null => {
    if (candleData.length < 4) return null;
    const recent = candleData.slice(-4);
    const closes = recent.map(c => c.close);
    const avgPrice = closes.reduce((a, b) => a + b, 0) / closes.length;
    const ema3 = calculateEMA(closes, 3);
    const slope = closes[3] - closes[2];
    const threshold = avgPrice * 0.001; // 0.1% move threshold

    if (slope > threshold && closes[3] > ema3) {
      return { type: 'BUY', pattern: 'Line Momentum Up', confidence: 70, time: recent[3].time, price: recent[3].close, expiry: 60 };
    }
    if (slope < -threshold && closes[3] < ema3) {
      return { type: 'SELL', pattern: 'Line Momentum Down', confidence: 70, time: recent[3].time, price: recent[3].close, expiry: 60 };
    }
    return null;
  };

  // Analyze consolidation and suggest direction
  const analyzeConsolidation = (candleData: Candle[]): ConsolidationAnalysis => {
    if (candleData.length === 0) return { consolidation: false, green: 0, red: 0 };
    
    // Always count all candles for the stats display
    const allGreenCount = candleData.filter(c => c.isBullish).length;
    const allRedCount = candleData.length - allGreenCount;
    
    // Only analyze consolidation if we have enough data
    if (candleData.length < 8) {
      return { 
        consolidation: false, 
        green: allGreenCount, 
        red: allRedCount 
      };
    }
    
    const recent = candleData.slice(-8);
    const priceRange = Math.max(...recent.map(c => c.high)) - Math.min(...recent.map(c => c.low));
    const avgPrice = recent.reduce((sum, c) => sum + c.close, 0) / recent.length;
    const volatility = priceRange / avgPrice;
    
    const isConsolidating = volatility < 0.015;
    
    return {
      consolidation: isConsolidating,
      green: allGreenCount,
      red: allRedCount,
      suggestion: isConsolidating ? (allGreenCount > allRedCount ? 'BUY' : 'SELL') : null
    };
  };

  // Update candles and analyze
  useEffect(() => {
    if (isRunning && isSharing) {
      intervalRef.current = setInterval(() => {
        setCandles(prev => {
          try {
            const lastClose = prev.length > 0 ? prev[prev.length - 1].close : null;
            const newCandle = generateCandle(lastClose);
            
            // Validate the generated candle
            if (!validateCandle(newCandle)) {
              console.warn('Invalid candle generated, skipping...');
              return prev;
            }
            
            const updated = [...prev, newCandle].slice(-maxCandles);
            
            // Analyze patterns with error handling
            const pattern = withErrorHandling(chartMode === 'line' ? analyzeLine : analyzePattern, 'Pattern analysis failed')(updated);
            const consol = withErrorHandling(analyzeConsolidation, 'Consolidation analysis failed')(updated);
            
            if (consol) {
              setMarketStats(consol);
            }
            
            // Update technical indicators
            updateTechnicalIndicators(updated);
            
            // Calculate current RSI for signal generation (to avoid state timing issues)
            const closePrices = updated.map(c => c.close);
            const currentRSI = calculateRSI(closePrices);
            
            // Signal generation logic: Generate BUY/SELL signals based on patterns regardless of RSI
            if (pattern) {
               // Generate BUY/SELL signals based on detected patterns
               const signal: TradingSignal = {
                 ...pattern,
                 time: newCandle.time,
                 price: newCandle.close,
                 expiry: tradeExpiry
               };
               
               // Always record the signal in history
               if (validateSignal(signal)) {
                 recordTrade(signal);
               }
               
               // Only set as active signal and play sounds if it meets trading criteria
               if (validateSignal(signal) && signal.confidence >= minConfidence && isRSITradeable(currentRSI)) {
                 setSignal(signal);
                 playSignalSound(signal.type);
                 playConfidenceAudio(signal.confidence);
               }
             } else if (consol?.consolidation && consol.suggestion && !isRSIConsolidation(currentRSI)) {
               // Generate consolidation breakout signals when RSI is outside consolidation range
               const signal: TradingSignal = {
                 type: consol.suggestion,
                 pattern: 'Consolidation Breakout',
                 confidence: 60,
                 time: newCandle.time,
                 price: newCandle.close,
                 expiry: tradeExpiry,
                 isConsolidation: true
               };
               
               // Always record the signal in history
               if (validateSignal(signal)) {
                 recordTrade(signal);
               }
               
               // Only set as active signal and play sounds if it meets trading criteria
               if (validateSignal(signal) && signal.confidence >= minConfidence && isRSITradeable(currentRSI)) {
                 setSignal(signal);
                 playSignalSound(signal.type);
                 playConfidenceAudio(signal.confidence);
               }
             } else if (isRSIConsolidation(currentRSI)) {
               // Generate consolidation signal when RSI is in consolidation range and no patterns detected
               const consolidationSignal: TradingSignal = {
                 type: 'CONSOLIDATION',
                 pattern: 'RSI Consolidation',
                 confidence: 70,
                 time: newCandle.time,
                 price: newCandle.close,
                 expiry: tradeExpiry,
                 isConsolidation: true
               };
               
               // Always record the signal in history
               if (validateSignal(consolidationSignal)) {
                 recordTrade(consolidationSignal);
               }
               
               // Only set as active signal and play sounds if it meets trading criteria
               if (validateSignal(consolidationSignal) && consolidationSignal.confidence >= minConfidence) {
                 setSignal(consolidationSignal);
                 playSignalSound(consolidationSignal.type);
               }
             }

             // Check for expired trades and update results
             const currentTime = new Date();
             tradeHistory.forEach(trade => {
               if (trade.result === 'PENDING' && currentTime >= trade.expiryTime) {
                 updateTradeResult(trade.id, newCandle.close);
               }
             });
            
            return updated;
          } catch (error) {
            console.error('Error in candle processing:', error);
            setSystemError(`Data processing error: ${error instanceof Error ? error.message : 'Unknown error'}`);
            return prev;
          }
        });
      }, timeframe * 1000);
    }
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, isSharing, timeframe, tradeExpiry, chartMode]);

  const toggleBot = () => {
    console.log('toggleBot clicked, current isRunning:', isRunning);
    if (!isRunning) {
      initAudioContext(); // Initialize audio context when starting
      playInitiationSound(); // Play initiation sound when starting
      playBackgroundAmbience(); // Start background ambience when bot starts
    } else {
      stopBackgroundAmbience(); // Stop background ambience when bot stops
    }
    setIsRunning(!isRunning);
    console.log('toggleBot setting isRunning to:', !isRunning);
  };
  
  // Clear localStorage function
  const clearLocalStorage = () => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  };

  // Mode switching function
  const switchDisplayMode = () => {
    const modes: DisplayMode[] = ['CHART', 'HISTORY', 'ANALYTICS', 'COMPACT'];
    const currentIndex = modes.indexOf(displayMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setDisplayMode(modes[nextIndex]);
  };

  // Get mode display info
  const getModeInfo = (mode: DisplayMode) => {
    switch (mode) {
      case 'CHART':
        return { icon: '📈', label: 'Chart View' };
      case 'HISTORY':
        return { icon: '📊', label: 'Trade History' };
      case 'ANALYTICS':
        return { icon: '📋', label: 'Analytics' };
      case 'COMPACT':
        return { icon: '📱', label: 'Compact View' };
      default:
        return { icon: '📈', label: 'Chart View' };
    }
  };

  const saveBotSettings = () => {
    const settings = { soundEnabled, minConfidence };
    saveToLocalStorage(STORAGE_KEYS.BOT_SETTINGS, settings);
  };

  // Save settings when they change
  useEffect(() => {
    saveBotSettings();
  }, [soundEnabled, minConfidence]);

  // Hide loader after 4 candles are created and show toast notification
  useEffect(() => {
    if (showLoader && candles.length >= 4) {
      setShowLoader(false);
      // Show toast notification when loader disappears
      if (sessionStartTime) {
        setShowToast(true);
      }
      // Start the session timer using selected duration
      setTimerActive(true);
      setTimeRemaining(sessionDuration);
    }
  }, [candles.length, showLoader, sessionStartTime]);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Timer countdown logic
  useEffect(() => {
    let timer: number;
    
    if (timerActive && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimerActive(false);
            // Trigger session end sequence when timer reaches 00:00
            handleSessionEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timerActive, timeRemaining]);

  const reset = () => {
    setCandles([]);
    setSignal(null);
    setMarketStats({ green: 0, red: 0, consolidation: false });
    setIsRunning(false);
    stopBackgroundAmbience(); // Stop background ambience when resetting
    setTradeHistory([]);
    setMacdHistory([]);
    const emptyStats = {
      totalTrades: 0,
      winRate: 0,
      totalProfit: 0,
      avgProfit: 0,
      bestTrade: 0,
      worstTrade: 0,
      winStreak: 0,
      lossStreak: 0,
      currentStreak: 0,
      currentStreakType: 'NONE' as 'WIN' | 'LOSS' | 'NONE'
    };
    setPerformanceStats(emptyStats);
    setShowToast(false);
    setSessionStartTime(null);
    setTimerActive(false);
    setTimeRemaining(sessionDuration); // Reset to selected session duration
    
    // Clear localStorage
    clearLocalStorage();
  };

  // Handle power off sequence: play audio then disconnect chart and reset
  const handlePowerOff = async () => {
    try {
      // Play power off audio first
      await playPowerOffAudio();
      
      // Wait for audio to finish (approximately 2 seconds) then disconnect chart and reset
      setTimeout(() => {
        // Disconnect chart if connected
        if (isSharing) {
          stopScreenShare();
        }
        
        // Then trigger reset
        reset();
      }, 2000);
    } catch (error) {
      console.error('Error during power off sequence:', error);
      // If audio fails, still proceed with chart disconnection and reset
      if (isSharing) {
        stopScreenShare();
      }
      reset();
    }
  };

  // Share screen functionality
  const startScreenShare = async () => {
    try {
      setShareError(null);
      
      // Check if screen sharing API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        setShareError('Screen sharing is not supported in your browser');
        return;
      }

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
        },
        audio: false
      });

      setIsSharing(true);
      setShowLoader(true); // Show loader when screen sharing starts
      setCandles([]); // Clear existing candles to start fresh
      setSessionStartTime(new Date()); // Capture session start time
      playChartConnectedAudio(); // Play chart connected sound when screen sharing starts
try {
  const created = await createSession({
    mode: sessionMode,
    durationSeconds: sessionDuration,
    startedAt: new Date().toISOString(),
    status: 'ACTIVE',
  });
  setSessionDocId(created.$id);
} catch (e) {
  console.error('Failed to create session document:', e);
}
      
      // Play scan in progress audio after 5 seconds of loader display
      setTimeout(() => {
        playScanInProgressAudio();
      }, 5000);

      // Handle when user stops sharing
      stream.getVideoTracks()[0].addEventListener('ended', () => {
        setIsSharing(false);
        playChartDisconnectedAudio(); // Play chart disconnected sound when screen sharing ends
      });

      // Store stream reference to stop later if needed
      chartRef.current = stream;

    } catch (err) {
      const error = err as Error;
      if (error.name === 'NotAllowedError') {
        setShareError('Screen sharing permission denied');
      } else if (error.name === 'NotFoundError') {
        setShareError('No screen available to share');
      } else {
        setShareError('Failed to start screen sharing: ' + error.message);
      }
      setIsSharing(false);
    }
  };

  const stopScreenShare = () => {
    if (chartRef.current) {
      chartRef.current.getTracks().forEach(track => track.stop());
      chartRef.current = null;
    }
    setIsSharing(false);
    playChartDisconnectedAudio(); // Play chart disconnected sound when screen sharing stops
  };

  // Calculate scale for chart
  const getChartScale = (): ChartScale => {
    if (candles.length === 0) return { min: 0, max: 100 };
    const allPrices = candles.flatMap(c => [c.high, c.low]);
    const min = Math.min(...allPrices);
    const max = Math.max(...allPrices);
    const padding = (max - min) * 0.1;
    return { min: min - padding, max: max + padding };
  };

  const scale = getChartScale();
  const chartHeight = 300;
  const candleWidth = 25;
  const candleGap = 5;

  const priceToY = (price: number): number => {
    return chartHeight - ((price - scale.min) / (scale.max - scale.min)) * chartHeight;
  };

  return (
    <div className="min-h-screen bg-black p-6 pt-12 relative overflow-hidden">
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="fixed top-0 left-0 w-full h-full object-contain z-0"
        style={{ 
          filter: 'brightness(0.9) contrast(0.9)',
          transform: 'scale(1.1)',
          transformOrigin: 'center center'
        }}
      >
        <source src={backgroundVideo} type="video/mp4" />
      </video>
      
      <div className="max-w-7xl mx-auto gold-animated-border relative z-10">
        <div className="gold-animated-border-inner p-6">
        {/* Header */}
        <div className="rounded-2xl p-6 mb-6 border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl shadow-xl shadow-black/40 relative">
          {/* Session Timer */}
          {timerActive && (
            <div className="absolute top-2 left-2 z-20">
              <div className="rounded-xl px-3 py-2 border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl shadow-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <div className="text-blue-400 font-semibold text-xs">Session</div>
                  <div className="text-slate-300 text-sm font-mono">
                    {Math.floor(timeRemaining / 60).toString().padStart(2, '0')}:
                    {(timeRemaining % 60).toString().padStart(2, '0')}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="absolute top-12 right-6">
             {isRunning ? (
               <div className="radio-input" style={{ transform: 'scale(0.6)', transformOrigin: 'top right', zIndex: 20 }}>
                 <label className="label">
                   <input 
                     value="mode-1" 
                     name="mode-radio-header" 
                     id="mode-1-header" 
                     type="radio" 
                     checked={sessionMode === 'MODE_1'}
                     onChange={() => { 
                       playMode1Audio(); 
                       setSessionDuration(15 * 60); 
                       setTimeRemaining(15 * 60); 
                       setSessionMode('MODE_1'); 
                     }} 
                   />
                   <span className="text">Mode 1</span>
                 </label>
                 <label className="label">
                   <input 
                     value="mode-2" 
                     name="mode-radio-header" 
                     id="mode-2-header" 
                     type="radio" 
                     checked={sessionMode === 'MODE_2'}
                     onChange={() => { 
                       playMode2Audio(); 
                       setSessionDuration(30 * 60); 
                       setTimeRemaining(30 * 60); 
                       setSessionMode('MODE_2'); 
                     }} 
                   />
                   <span className="text">Mode 2</span>
                 </label>
                 <label className="label">
                   <input 
                     value="mode-3" 
                     name="mode-radio-header" 
                     id="mode-3-header" 
                     type="radio" 
                     checked={sessionMode === 'MODE_3'}
                     onChange={() => { 
                       playMode3Audio(); 
                       setSessionDuration(60 * 60); 
                       setTimeRemaining(60 * 60); 
                       setSessionMode('MODE_3'); 
                     }} 
                   />
                   <span className="text">Mode 3</span>
                 </label>
               </div>
             ) : (
               <div className="px-3 py-1 rounded-full border border-white/10 bg-white/5 text-slate-300 text-xs tracking-wide">Inactive</div>
             )}
           </div>
          <h1 className="text-3xl font-bold text-white mb-2 text-center font-topsecret">Anti-Trade-Bot</h1>
          <p className="text-slate-300 text-center">Real-time {chartMode === 'line' ? 'line' : 'candlestick'} analysis with {tradeExpiry}s trade expiry</p>
        </div>

        {/* Controls */}
        <div className="rounded-2xl p-8 mb-6 border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl shadow-xl shadow-black/40">
          {/* Main Action Buttons Row */}
          <div className="flex flex-wrap gap-3 items-center justify-center mb-6">
            <button
              onClick={toggleBot}
              className={`launch-button ${isRunning ? 'launch-button--running' : ''}`}
            >
              <div className="launch-outline"></div>
              <div className={`launch-state ${isRunning ? 'launch-state--sent' : 'launch-state--default'}`}>
                <div className="launch-icon">
                  {isRunning ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="6" y="4" width="4" height="16" fill="currentColor"/>
                      <rect x="14" y="4" width="4" height="16" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 5v14l11-7z" fill="currentColor"/>
                    </svg>
                  )}
                </div>
                <p>
                  {isRunning ? (
                    <>
                      <span style={{"--i": 0} as React.CSSProperties}>R</span>
                      <span style={{"--i": 1} as React.CSSProperties}>U</span>
                      <span style={{"--i": 2} as React.CSSProperties}>N</span>
                      <span style={{"--i": 3} as React.CSSProperties}>N</span>
                      <span style={{"--i": 4} as React.CSSProperties}>I</span>
                      <span style={{"--i": 5} as React.CSSProperties}>N</span>
                      <span style={{"--i": 6} as React.CSSProperties}>G</span>
                    </>
                  ) : (
                    <>
                      <span style={{"--i": 0} as React.CSSProperties}>S</span>
                      <span style={{"--i": 1} as React.CSSProperties}>T</span>
                      <span style={{"--i": 2} as React.CSSProperties}>A</span>
                      <span style={{"--i": 3} as React.CSSProperties}>R</span>
                      <span style={{"--i": 4} as React.CSSProperties}>T</span>
                    </>
                  )}
                </p>
              </div>
            </button>
            
            <button
              onClick={handlePowerOff}
              disabled={!isRunning}
              className={`luxury-button ${!isRunning ? 'luxury-button--disabled' : ''}`}
            >
              <span className="button-text flex items-center gap-2">
                <RotateCcw size={20} />
                Off
              </span>
              <span className="hover-bg"></span>
            </button>

            <button
               onClick={() => setSoundEnabled(!soundEnabled)}
               disabled={!isRunning}
               className={`luxury-button ${!isRunning ? 'luxury-button--disabled' : ''}`}
             >
               <span className="button-text flex items-center gap-2">
                 🔊 {soundEnabled ? 'Sound On' : 'Sound Off'}
               </span>
               <span className="hover-bg"></span>
             </button>

             <button
               onClick={switchDisplayMode}
               disabled={!isRunning}
               className={`luxury-button ${!isRunning ? 'luxury-button--disabled' : ''}`}
             >
               <span className="button-text flex items-center gap-2">
                 {getModeInfo(displayMode).icon} {getModeInfo(displayMode).label}
               </span>
               <span className="hover-bg"></span>
             </button>

            <button
              onClick={isSharing ? stopScreenShare : startScreenShare}
              disabled={!isRunning}
              className={`luxury-button ${!isRunning ? 'luxury-button--disabled' : ''}`}
            >
              <span className="button-text flex items-center gap-2">
                {isSharing ? <Monitor size={20} /> : <Share2 size={20} />}
                {isSharing ? 'Connected' : 'Connect Chart'}
              </span>
              <span className="hover-bg"></span>
            </button>

            <button
              onClick={async () => {
                try {
                  await account.deleteSession('current');
                  window.location.reload();
                } catch (err) {
                  console.error('Logout failed:', err);
                }
              }}
              className="luxury-button"
            >
              <span className="button-text flex items-center gap-2">
                <LogOut size={20} />
                Logout
              </span>
              <span className="hover-bg"></span>
            </button>
          </div>

          {/* Settings Controls Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 items-end">
            <div className="flex flex-col gap-2">
              {/*<label className="text-white font-medium text-sm text-right ml-12">Candle Timeframe:</label>*/}
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(Number(e.target.value))}
                disabled={isRunning}
                className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-lg border border-white/20 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all"
              >
                <option value={15} className="bg-slate-800 text-white">15 seconds</option>
                <option value={30} className="bg-slate-800 text-white">30 seconds</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              {/*<label className="text-white font-medium text-sm">Chart Mode:</label>*/}
              <select
                value={chartMode}
                onChange={(e) => setChartMode(e.target.value as 'candlestick' | 'line')}
                disabled={isRunning}
                className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-lg border border-white/20 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all"
              >
                <option value="candlestick" className="bg-slate-800 text-white">Candlesticks</option>
                <option value="line" className="bg-slate-800 text-white">Line</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              {/*<label className="text-white font-medium text-sm">Trade Expiry:</label>*/}
              <select
                value={tradeExpiry}
                onChange={(e) => setTradeExpiry(Number(e.target.value))}
                disabled={isRunning}
                className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-lg border border-white/20 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all"
              >
                <option value={5} className="bg-slate-800 text-white">5 seconds</option>
                <option value={7} className="bg-slate-800 text-white">7 seconds</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              {/*<label className="text-white font-medium text-sm">Min Confidence:</label>*/}
              <div className="flex flex-col gap-1">
                <input
                  type="range"
                  min={50}
                  max={95}
                  step={5}
                  value={minConfidence}
                  onChange={(e) => setMinConfidence(Number(e.target.value))}
                  className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((minConfidence - 50) / 45) * 100}%, rgba(255,255,255,0.2) ${((minConfidence - 50) / 45) * 100}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
                <div className="text-xs text-white/80 text-center">Currently: {minConfidence}%</div>
              </div>
            </div>

            <div className="flex flex-col gap-2 text-center">
              {/*<label className="text-white font-medium text-sm">Trade Expiry</label>*/}
              <div className="text-2xl font-bold text-blue-400">{tradeExpiry}s</div>
            </div>
          </div>
        </div>

        {/* ATM Badge - Fun Marketable Section */}
        <div className="rounded-2xl p-6 mb-6 border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl shadow-xl shadow-black/40">
          <h3 className="text-2xl font-bold text-white mb-4 text-center">CUZIN CASH OFFICIAL TRADING BADGE</h3>
          <div className="flex items-center justify-center">
            <div className="flip-card">
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <p className="heading_8264">ATM COURSE</p>
                  <svg className="logo" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="36" height="36" viewBox="0 0 48 48">
                    <path fill="#ff9800" d="M32 10A14 14 0 1 0 32 38A14 14 0 1 0 32 10Z"></path><path fill="#d50000" d="M16 10A14 14 0 1 0 16 38A14 14 0 1 0 16 10Z"></path><path fill="#ff3d00" d="M18,24c0,4.755,2.376,8.95,6,11.48c3.624-2.53,6-6.725,6-11.48s-2.376-8.95-6-11.48 C20.376,15.05,18,19.245,18,24z"></path>
                  </svg>
                  <svg version="1.1" className="chip" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="30px" height="30px" viewBox="0 0 50 50" xmlSpace="preserve">
                    <image id="image0" width="50" height="50" x="0" y="0" href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAMAAAAp4XiDAAAABGdBTUEAALGPC/xhBQAAACBjSFJN
              AAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAB6VBMVEUAAACNcTiVeUKVeUOY
              fEaafEeUeUSYfEWZfEaykleyklaXe0SWekSZZjOYfEWYe0WXfUWXe0WcgEicfkiXe0SVekSXekSW
              ekKYe0a9nF67m12ZfUWUeEaXfESVekOdgEmVeUWWekSniU+VeUKVeUOrjFKYfEWliE6WeESZe0GS
              e0WYfES7ml2Xe0WXeESUeEOWfEWcf0eWfESXe0SXfEWYekSVeUKXfEWxklawkVaZfEWWekOUekOW
              ekSYfESZe0eXekWYfEWZe0WZe0eVeUSWeETAnmDCoWLJpmbxy4P1zoXwyoLIpWbjvXjivnjgu3bf
              u3beunWvkFWxkle/nmDivXiWekTnwXvkwHrCoWOuj1SXe0TEo2TDo2PlwHratnKZfEbQrWvPrWua
              fUfbt3PJp2agg0v0zYX0zYSfgkvKp2frxX7mwHrlv3rsxn/yzIPgvHfduXWXe0XuyIDzzISsjVO1
              lVm0lFitjVPzzIPqxX7duna0lVncuHTLqGjvyIHeuXXxyYGZfUayk1iyk1e2lln1zYTEomO2llrb
              tnOafkjFpGSbfkfZtXLhvHfkv3nqxH3mwXujhU3KqWizlFilh06khk2fgkqsjlPHpWXJp2erjVOh
              g0yWe0SliE+XekShhEvAn2D///+gx8TWAAAARnRSTlMACVCTtsRl7Pv7+vxkBab7pZv5+ZlL/UnU
              /f3SJCVe+Fx39naA9/75XSMh0/3SSkia+pil/KRj7Pr662JPkrbP7OLQ0JFOijI1MwAAAAFiS0dE
              orDd34wAAAAJcEhZcwAACxMAAAsTAQCanBgAAAAHdElNRQfnAg0IDx2lsiuJAAACLElEQVRIx2Ng
              GAXkAUYmZhZWPICFmYkRVQcbOwenmzse4MbFzc6DpIGXj8PD04sA8PbhF+CFaxEU8iWkAQT8hEVg
              OkTF/InR4eUVICYO1SIhCRMLDAoKDvFDVhUaEhwUFAjjSUlDdMiEhcOEItzdI6OiYxA6YqODIt3d
              I2DcuDBZsBY5eVTr4xMSYcyk5BRUOXkFsBZFJTQnp6alQxgZmVloUkrKYC0qqmji2WE5EEZuWB6a
              lKoKdi35YQUQRkFYPpFaCouKIYzi6EDitJSUlsGY5RWVRGjJLyxNy4ZxqtIqqvOxaVELQwZFZdkI
              JVU1RSiSalAt6rUwUBdWG1CP6pT6gNqwOrgCdQyHNYR5YQFhDXj8MiK1IAeyN6aORiyBjByVTc0F
              qBoKWpqwRCVSgilOaY2OaUPw29qjOzqLvTAchpos47u6EZyYnngUSRwpuTe6D+6qaFQdOPNLRzOM
              1dzhRZyW+CZouHk3dWLXglFcFIflQhj9YWjJGlZcaKAVSvjyPrRQ0oQVKDAQHlYFYUwIm4gqExGm
              BSkutaVQJeomwViTJqPK6OhCy2Q9sQBk8cY0DxjTJw0lAQWK6cOKfgNhpKK7ZMpUeF3jPa28BCET
              amiEqJKM+X1gxvWXpoUjVIVPnwErw71nmpgiqiQGBjNzbgs3j1nus+fMndc+Cwm0T52/oNR9lsdC
              S24ra7Tq1cbWjpXV3sHRCb1idXZ0sGdltXNxRateRwHRAACYHutzk/2I5QAAACV0RVh0ZGF0ZTpj
              cmVhdGUAMjAyMy0wMi0xM1QwODoxNToyOSswMDowMEUnN7UAAAAldEVYdGRhdGU6bW9kaWZ5ADIw
              MjMtMDItMTNUMDg6MTU6MjkrMDA6MDA0eo8JAAAAKHRFWHRkYXRlOnRpbWVzdGFtcAAyMDIzLTAy
              LTEzVDA4OjE1OjI5KzAwOjAwY2+u1gAAAABJRU5ErkJggg=="></image>
                  </svg>
                  <svg version="1.1" className="contactless" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="20px" height="20px" viewBox="0 0 50 50" xmlSpace="preserve">
                    <image id="image0" width="50" height="50" x="0" y="0" href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAQAAAC0NkA6AAAABGdBTUEAALGPC/xhBQAAACBjSFJN
              AAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAAmJLR0QA/4ePzL8AAAAJcEhZ
              cwAACxMAAAsTAQCanBgAAAAHdElNRQfnAg0IEzgIwaKTAAADDklEQVRYw+1XS0iUURQ+f5qPyjQf
              lGRFEEFK76koKGxRbWyVVLSOgsCgwjZBJJYuKogSIoOonUK4q3U0WVBWFPZYiIE6kuArG3VGzK/F
              fPeMM/MLt99/NuHdfPd888/57jn3nvsQWWj/VcMlvMMd5KRTogqx9iCdIjUUmcGR9ImUYowyP3xN
              GQJoRLVaZ2DaZf8kyjEJALhI28ELioyiwC+Rc3QZwRYyO/DH51hQgWm6DMIh10KmD4u9O16K49it
              VoPOAmcGAWWOepXIRScAoJZ2Frro8oN+EyTT6lWkkg6msZfMSR35QTJmjU0g15tIGSJ08ZZMJkJk
              HpNZgSkyXosS13TkJpZ62mPIJvOSzC1bp8vRhhCakEk7G9/o4gmZdbpsTcKu0m63FbnBP9Qrc15z
              bkbemfgNDtEOI8NO5L5O9VYyRYgmJayZ9nPaxZrSjW4+F6Uw9yQqIiIZwhp2huQTf6OIvCZyGM6g
              DJBZbyXifJXr7FZjGXsdxADxI7HUJFB6iWvsIhFpkoiIiGTJfjJfiCuJg2ZEspq9EHGVpYgzKqwJ
              qSAOEwuJQ/pxPvE3cYltJCLdxBLiSKKIE5HxJKcTRNeadxfhDiuYw44zVs1dxKwRk/uCxIiQkxKB
              sSctRVAge9g1E15EHE6yRUaJecRxcWlukdRIbGFOSZCMWQA/iWauIP3slREHXPyliqBcrrD71Amz
              Z+rD1Mt2Yr8TZc/UR4/YtFnbijnHi3UrN9vKQ9rPaJf867ZiaqDB+czeKYmd3pNa6fuI75MiC0uX
              XSR5aEMf7s7a6r/PudVXkjFb/SsrCRfROk0Fx6+H1i9kkTGn/E1vEmt1m089fh+RKdQ5O+xNJPUi
              cUIjO0Dm7HwvErEr0YxeibL1StSh37STafE4I7zcBdRq1DiOkdmlTJVnkQTBTS7X1FYyvfO4piaI
              nKbDCDaT2anLudYXCRFsQBgAcIF2/Okwgvz5+Z4tsw118dzruvIvjhTB+HOuWy8UvovEH6beitBK
              xDyxm9MmISKCWrzB7bSlaqGlsf0FC0gMjzTg6GgAAAAldEVYdGRhdGU6Y3JlYXRlADIwMjMtMDIt
              MTNUMDg6MTk6NTYrMDA6MDCjlq7LAAAAJXRFWHRkYXRlOm1vZGlmeQAyMDIzLTAyLTEzVDA4OjE5
              OjU2KzAwOjAw0ssWdwAAACh0RVh0ZGF0ZTp0aW1lc3RhbXAAMjAyMy0wMi0xM1QwODoxOTo1Nisw
              MDowMIXeN6gAAAAASUVORK5CYII="></image>
                  </svg>
                  <p className="number">3217 0784 3204 2021</p>
                  <p className="valid_thru">VALID THRU</p>
                  <p className="date_8264">07 / 27</p>
                  <p className="name">CUZIN CASH</p>
                </div>
                <div className="flip-card-back">
                  <div className="strip"></div>
                  <div className="mstrip"></div>
                  <div className="sstrip">
                    <p className="code">143</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Share Status/Error Message */}
        {isSharing && (
          <div className="bg-blue-900/30 border-2 border-blue-500 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <Monitor size={24} className="text-blue-400 animate-pulse" />
              <div>
                <div className="text-blue-400 font-semibold">Chart Connection Made</div>
                <div className="text-slate-300 text-sm">Your chart is now being shared. Click "Connected" to stop.</div>
              </div>
            </div>
          </div>
        )}

        {shareError && (
          <div className="bg-red-900/30 border-2 border-red-500 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="text-red-400 font-semibold">Screen Share Error</div>
            </div>
            <div className="text-slate-300 text-sm mt-1">{shareError}</div>
          </div>
        )}

        {systemError && (
          <div className="bg-red-900/30 border-2 border-red-500 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-red-400 font-semibold">System Error</div>
                <div className="text-slate-300 text-sm mt-1">{systemError}</div>
              </div>
              <button
                onClick={() => setSystemError(null)}
                className="panel-button text-sm"
              >
                <span className="button-text">
                  Dismiss
                </span>
                <span className="hover-bg"></span>
              </button>
            </div>
          </div>
        )}

        {/* Signal Display */}
        {signal && (
          <div className={`rounded-lg p-6 mb-6 border-2 ${
            signal.type === 'BUY'
              ? 'bg-green-900/30 border-green-500'
              : signal.type === 'SELL'
              ? 'bg-red-900/30 border-red-500'
              : 'bg-yellow-900/30 border-yellow-500'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {signal.type === 'BUY' ? (
                  <TrendingUp size={48} className="text-green-400" />
                ) : signal.type === 'SELL' ? (
                  <TrendingDown size={48} className="text-red-400" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-yellow-400"></div>
                  </div>
                )}
                <div>
                  <div className={`text-3xl font-bold ${
                    signal.type === 'BUY' 
                      ? 'text-green-400' 
                      : signal.type === 'SELL'
                      ? 'text-red-400'
                      : 'text-yellow-400'
                  }`}>
                    {signal.type} SIGNAL
                  </div>
                  <div className="text-slate-300 text-lg mt-1">{signal.pattern}</div>
                  {signal.isConsolidation && (
                    <div className="text-yellow-400 text-sm mt-1">
                      Based on consolidation analysis
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-slate-400 text-sm">Confidence</div>
                <div className={`text-3xl font-bold ${getConfidenceColor(signal.confidence)}`}>{signal.confidence}%</div>
                <div className="text-sm text-slate-400">Min Required</div>
                <div className="text-xl font-semibold text-white">{minConfidence}%</div>
                <div className="text-slate-400 text-sm mt-2">Price: ${signal.price}</div>
                <div className="text-slate-400 text-sm">Time: {signal.time}</div>
              </div>
            </div>
          </div>
        )}

        {/* Market Stats - Hidden during loader and only shown in CHART mode */}
        {!showLoader && displayMode === 'CHART' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="rounded-2xl p-6 border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl shadow-xl shadow-black/40">
              <div className="text-slate-300 text-sm mb-1">Green Candles</div>
              <div className="text-2xl font-bold text-green-400">{marketStats.green}</div>
            </div>
            <div className="rounded-2xl p-6 border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl shadow-xl shadow-black/40">
              <div className="text-slate-300 text-sm mb-1">Red Candles</div>
              <div className="text-2xl font-bold text-red-400">{marketStats.red}</div>
            </div>
            <div className="rounded-2xl p-6 border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl shadow-xl shadow-black/40">
              <div className="text-slate-300 text-sm mb-1">Market Status</div>
              <div className={`text-xl font-bold ${
                marketStats.consolidation ? 'text-yellow-400' : 'text-blue-400'
              }`}>
                {marketStats.consolidation ? (
                  <span className="flex items-center gap-2">
                    <Minus size={20} /> Consolidating
                    <span className="text-sm">
                      ({marketStats.green > marketStats.red ? '↑ Bullish bias' : '↓ Bearish bias'})
                    </span>
                  </span>
                ) : (
                  'Trending'
                 )}
               </div>
            </div>
          </div>
        )}

        {/* Technical Indicators */}
         <div className="rounded-2xl p-6 border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl shadow-xl shadow-black/40">
           <h3 className="text-xl font-bold text-white mb-4">Technical Indicators</h3>
           <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
             <div className="text-center">
               <div className={`text-2xl font-bold ${
                 technicalIndicators.rsi > 70 ? 'text-red-400' : 
                 technicalIndicators.rsi < 30 ? 'text-green-400' : 'text-yellow-400'
               }`}>
                 {technicalIndicators.rsi.toFixed(1)}
               </div>
               <div className="text-sm text-gray-400">RSI (17)</div>
               <div className="text-sm text-slate-300">
                 {isRSIConsolidation(technicalIndicators.rsi) ? 'Consolidation (47–53)' : 'Tradeable (<47% or >53%)'}
               </div>
               <div className="text-sm text-gray-400">
                 {isRSIConsolidation(technicalIndicators.rsi) ? 'Consolidation' : (technicalIndicators.rsi > 70 ? 'Overbought' : technicalIndicators.rsi < 30 ? 'Oversold' : 'Neutral')}
               </div>
             </div>
             <div className="text-center">
               <div className={`text-lg font-bold ${technicalIndicators.macd.histogram > 0 ? 'text-green-400' : 'text-red-400'}`}>
                 {technicalIndicators.macd.macd.toFixed(4)}
               </div>
               <div className="text-sm text-gray-400">MACD</div>
               <div className="text-xs text-gray-500">
                 {technicalIndicators.macd.histogram > 0 ? 'Bullish' : 'Bearish'}
               </div>
             </div>
             <div className="text-center">
               <div className="text-lg font-bold text-blue-400">
                 ${technicalIndicators.sma20.toFixed(4)}
               </div>
               <div className="text-sm text-gray-400">SMA (20)</div>
             </div>
             <div className="text-center">
               <div className="text-lg font-bold text-purple-400">
                 ${technicalIndicators.ema5.toFixed(4)}
               </div>
               <div className="text-sm text-gray-400">EMA (5)</div>
             </div>
             <div className="text-center">
               <div className="text-lg font-bold text-orange-400">
                 ${technicalIndicators.ema13.toFixed(4)}
               </div>
               <div className="text-sm text-gray-400">EMA (13)</div>
             </div>
             <div className="text-center">
               <div className="text-lg font-bold text-pink-400">
                 ${technicalIndicators.ema62.toFixed(4)}
               </div>
               <div className="text-sm text-gray-400">EMA (62)</div>
             </div>
           </div>
         </div>

         {/* Performance Statistics */}
         {(displayMode === 'ANALYTICS' || displayMode === 'HISTORY') && (
           <div className="rounded-2xl p-6 mb-6 border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl shadow-xl shadow-black/40 mt-8">
             <h3 className="text-xl font-bold text-white mb-4">Performance Analytics</h3>
             <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
               <div className="text-center">
                 <div className="text-2xl font-bold text-blue-400">{performanceStats.totalTrades}</div>
                 <div className="text-sm text-gray-400">Total Trades</div>
               </div>
               <div className="text-center">
                 <div className={`text-2xl font-bold ${performanceStats.winRate >= 60 ? 'text-green-400' : performanceStats.winRate >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                   {performanceStats.winRate.toFixed(1)}%
                 </div>
                 <div className="text-sm text-gray-400">Win Rate</div>
               </div>
               <div className="text-center">
                 <div className={`text-2xl font-bold ${performanceStats.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                   {performanceStats.totalProfit >= 0 ? '+' : ''}{performanceStats.totalProfit.toFixed(2)}
                 </div>
                 <div className="text-sm text-gray-400">Total Profit</div>
               </div>
               <div className="text-center">
                 <div className={`text-2xl font-bold ${performanceStats.currentStreakType === 'WIN' ? 'text-green-400' : performanceStats.currentStreakType === 'LOSS' ? 'text-red-400' : 'text-gray-400'}`}>
                   {performanceStats.currentStreak}
                 </div>
                 <div className="text-sm text-gray-400">
                   {performanceStats.currentStreakType === 'NONE' ? 'No Streak' : `${performanceStats.currentStreakType} Streak`}
                 </div>
               </div>
             </div>
           </div>
         )}

         {/* Trade History */}
         {displayMode === 'HISTORY' && (
           <div className="bg-slate-800 rounded-lg p-6 mb-6 border border-slate-700 mt-8">
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-xl font-bold text-white">Trade History</h3>
               <div className="flex items-center gap-3 text-sm text-gray-400">
                <div>
                  Session Trades: <span className="text-white font-semibold">{tradeHistory.length}</span>
                </div>
              </div>
             </div>
             {tradeHistory.length === 0 ? (
               <div className="text-center text-gray-400 py-8">
                 No trades recorded yet. Start the bot to begin trading!
               </div>
             ) : (
               <div className="overflow-x-auto">
                 <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-track-slate-700 scrollbar-thumb-slate-500 hover:scrollbar-thumb-slate-400">
                   <table className="w-full text-sm">
                     <thead className="sticky top-0 bg-slate-800 z-10">
                       <tr className="border-b border-slate-600">
                         <th className="text-left text-gray-400 pb-2 pt-2">Time</th>
                         <th className="text-left text-gray-400 pb-2 pt-2">Signal</th>
                         <th className="text-left text-gray-400 pb-2 pt-2">Pattern</th>
                         <th className="text-left text-gray-400 pb-2 pt-2">Confidence</th>
                         <th className="text-left text-gray-400 pb-2 pt-2">Entry</th>
                         <th className="text-left text-gray-400 pb-2 pt-2">Exit</th>
                       </tr>
                     </thead>
                     <tbody>
                       {tradeHistory.map((trade) => (
                       <tr key={trade.id} className="border-b border-slate-700">
                         <td className="py-2 text-gray-300">
                           {trade.entryTime.toLocaleTimeString()}
                         </td>
                         <td className="py-2">
                           <span className={`px-2 py-1 rounded text-xs font-semibold ${
                             trade.signal.type === 'BUY' 
                               ? 'bg-green-600 text-white' 
                               : trade.signal.type === 'SELL'
                               ? 'bg-red-600 text-white'
                               : 'bg-yellow-600 text-white'
                           }`}>
                             {trade.signal.type}
                           </span>
                         </td>
                         <td className="py-2 text-gray-300 text-xs">
                           {trade.signal.pattern}
                         </td>
                         <td className={getConfidenceColor(trade.signal.confidence)}>
                           {trade.signal.confidence}%
                         </td>
                         <td className="py-2 text-gray-300">
                           ${trade.entryPrice.toFixed(4)}
                         </td>
                         <td className="py-2 text-gray-300">
                           {trade.exitPrice ? `$${trade.exitPrice.toFixed(4)}` : '-'}
                         </td>
                       </tr>
                       ))}
                     </tbody>
                   </table>
                 </div>
               </div>
             )}
           </div>
         )}

         {/* Chart */}
         {displayMode === 'CHART' && (
           <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 mt-8">
              <h2 className="text-xl font-bold text-white mb-4">Live Momentum Chart ({timeframe}s {chartMode === 'line' ? 'line' : 'candles'})</h2>
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <svg width={candles.length * (candleWidth + candleGap)} height={chartHeight} className="min-w-full">
                  {/* Grid lines */}
                  {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
                    const y = chartHeight * pct;
                    const price = scale.max - (scale.max - scale.min) * pct;
                    return (
                      <g key={i}>
                        <line
                          x1={0}
                          y1={y}
                          x2={candles.length * (candleWidth + candleGap)}
                          y2={y}
                          stroke="#334155"
                          strokeWidth={1}
                          strokeDasharray="4"
                        />
                        <text x={5} y={y - 5} fill="#94a3b8" fontSize={12}>
                          ${price.toFixed(2)}
                        </text>
                      </g>
                    );
                  })}

              {/* Candles */}
              {chartMode === 'line' ? (
                <path
                  d={candles.map((candle, i) => {
                    const x = i * (candleWidth + candleGap) + candleWidth / 2;
                    const y = priceToY(candle.close);
                    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                  }).join(' ')}
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth={2}
                />
              ) : (
                candles.map((candle, i) => {
                  const x = i * (candleWidth + candleGap);
                  const openY = priceToY(candle.open);
                  const closeY = priceToY(candle.close);
                  const highY = priceToY(candle.high);
                  const lowY = priceToY(candle.low);
                  const color = candle.isBullish ? '#10b981' : '#ef4444';
                  const bodyHeight = Math.abs(closeY - openY);

                  return (
                    <g key={i}>
                      {/* Wick */}
                      <line
                        x1={x + candleWidth / 2}
                        y1={highY}
                        x2={x + candleWidth / 2}
                        y2={lowY}
                        stroke={color}
                        strokeWidth={2}
                      />
                      {/* Body */}
                      <rect
                        x={x}
                        y={Math.min(openY, closeY)}
                        width={candleWidth}
                        height={Math.max(bodyHeight, 2)}
                        fill={color}
                        opacity={0.9}
                      />
                    </g>
                  );
                })
              )}]
            </svg>
          </div>
          {candles.length === 0 && (
            <div className="text-center text-slate-400 py-12">
              Start the bot to see live candlestick data
            </div>
          )}
        </div>
        )}

        {/* Compact Mode */}
        {displayMode === 'COMPACT' && (
          <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 mt-8">
            <h2 className="text-xl font-bold text-white mb-4">Trading Dashboard - Compact View</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Current Signal */}
              <div className="bg-slate-900 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-400 mb-2">Current Signal</h3>
                <div className={`text-lg font-bold ${
                  signal?.type === 'BUY' ? 'text-green-400' : 
                  signal?.type === 'SELL' ? 'text-red-400' : 
                  'text-yellow-400'
                }`}>
                  {signal?.type || 'WAITING'}
                </div>
              </div>

              {/* Current Price */}
              <div className="bg-slate-900 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-400 mb-2">Current Price</h3>
                <div className="text-lg font-bold text-white">
                  ${candles.length > 0 ? candles[candles.length - 1].close.toFixed(2) : '0.00'}
                </div>
              </div>

              {/* RSI */}
              <div className="bg-slate-900 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-400 mb-2">RSI</h3>
                <div className={`text-lg font-bold ${
                  technicalIndicators.rsi > 70 ? 'text-red-400' : 
                  technicalIndicators.rsi < 30 ? 'text-green-400' : 
                  'text-yellow-400'
                }`}>
                  {technicalIndicators.rsi.toFixed(1)}
                </div>
              </div>

              {/* Total Profit */}
              <div className="bg-slate-900 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-400 mb-2">Total Profit</h3>
                <div className={`text-lg font-bold ${
                  performanceStats.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {performanceStats.totalProfit >= 0 ? '+' : ''}${performanceStats.totalProfit.toFixed(2)}
                </div>
              </div>

              {/* Win Rate */}
              <div className="bg-slate-900 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-400 mb-2">Win Rate</h3>
                <div className={`text-lg font-bold ${
                  performanceStats.winRate >= 60 ? 'text-green-400' : 
                  performanceStats.winRate >= 40 ? 'text-yellow-400' : 
                  'text-red-400'
                }`}>
                  {performanceStats.winRate.toFixed(1)}%
                </div>
              </div>

              {/* Bot Status */}
              <div className="bg-slate-900 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-400 mb-2">Bot Status</h3>
                <div className={`text-lg font-bold ${
                  isRunning ? 'text-green-400' : 'text-red-400'
                }`}>
                  {isRunning ? 'RUNNING' : 'STOPPED'}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Loader Overlay */}
      {showLoader && (
        <div className="loader-overlay">
          <div className="main-container">
            <div className="loader">
              <svg viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="chipGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2d2d2d"></stop>
                    <stop offset="100%" stopColor="#0f0f0f"></stop>
                  </linearGradient>

                  <linearGradient id="textGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#eeeeee"></stop>
                    <stop offset="100%" stopColor="#888888"></stop>
                  </linearGradient>

                  <linearGradient id="pinGradient" x1="1" y1="0" x2="0" y2="0">
                    <stop offset="0%" stopColor="#bbbbbb"></stop>
                    <stop offset="50%" stopColor="#888888"></stop>
                    <stop offset="100%" stopColor="#555555"></stop>
                  </linearGradient>
                </defs>

                <g id="traces">
                  <path d="M100 100 H200 V210 H326" className="trace-bg"></path>
                  <path d="M100 100 H200 V210 H326" className="trace-flow purple"></path>

                  <path d="M80 180 H180 V230 H326" className="trace-bg"></path>
                  <path d="M80 180 H180 V230 H326" className="trace-flow blue"></path>

                  <path d="M60 260 H150 V250 H326" className="trace-bg"></path>
                  <path d="M60 260 H150 V250 H326" className="trace-flow yellow"></path>

                  <path d="M100 350 H200 V270 H326" className="trace-bg"></path>
                  <path d="M100 350 H200 V270 H326" className="trace-flow green"></path>

                  <path d="M700 90 H560 V210 H474" className="trace-bg"></path>
                  <path d="M700 90 H560 V210 H474" className="trace-flow blue"></path>

                  <path d="M740 160 H580 V230 H474" className="trace-bg"></path>
                  <path d="M740 160 H580 V230 H474" className="trace-flow green"></path>

                  <path d="M720 250 H590 V250 H474" className="trace-bg"></path>
                  <path d="M720 250 H590 V250 H474" className="trace-flow red"></path>

                  <path d="M680 340 H570 V270 H474" className="trace-bg"></path>
                  <path d="M680 340 H570 V270 H474" className="trace-flow yellow"></path>
                </g>

                <rect
                  x="330"
                  y="190"
                  width="140"
                  height="100"
                  rx="20"
                  ry="20"
                  fill="url(#chipGradient)"
                  stroke="#222"
                  strokeWidth="3"
                  filter="drop-shadow(0 0 6px rgba(0,0,0,0.8))"
                ></rect>

                <g>
                  <rect
                    x="322"
                    y="205"
                    width="8"
                    height="10"
                    fill="url(#pinGradient)"
                    rx="2"
                  ></rect>
                  <rect
                    x="322"
                    y="225"
                    width="8"
                    height="10"
                    fill="url(#pinGradient)"
                    rx="2"
                  ></rect>
                  <rect
                    x="322"
                    y="245"
                    width="8"
                    height="10"
                    fill="url(#pinGradient)"
                    rx="2"
                  ></rect>
                  <rect
                    x="322"
                    y="265"
                    width="8"
                    height="10"
                    fill="url(#pinGradient)"
                    rx="2"
                  ></rect>
                </g>

                <g>
                  <rect
                    x="470"
                    y="205"
                    width="8"
                    height="10"
                    fill="url(#pinGradient)"
                    rx="2"
                  ></rect>
                  <rect
                    x="470"
                    y="225"
                    width="8"
                    height="10"
                    fill="url(#pinGradient)"
                    rx="2"
                  ></rect>
                  <rect
                    x="470"
                    y="245"
                    width="8"
                    height="10"
                    fill="url(#pinGradient)"
                    rx="2"
                  ></rect>
                  <rect
                    x="470"
                    y="265"
                    width="8"
                    height="10"
                    fill="url(#pinGradient)"
                    rx="2"
                  ></rect>
                </g>

                <text
                  x="400"
                  y="240"
                  fontFamily="Arial, sans-serif"
                  fontSize="22"
                  fill="url(#textGradient)"
                  textAnchor="middle"
                  alignmentBaseline="middle"
                >
                  Chart Scan
                </text>

                <circle cx="100" cy="100" r="5" fill="black"></circle>
                <circle cx="80" cy="180" r="5" fill="black"></circle>
                <circle cx="60" cy="260" r="5" fill="black"></circle>
                <circle cx="100" cy="350" r="5" fill="black"></circle>

                <circle cx="700" cy="90" r="5" fill="black"></circle>
                <circle cx="740" cy="160" r="5" fill="black"></circle>
                <circle cx="720" cy="250" r="5" fill="black"></circle>
                <circle cx="680" cy="340" r="5" fill="black"></circle>
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && sessionStartTime && (
        <div className="fixed bottom-6 left-6 z-50 animate-in slide-in-from-bottom-2 duration-300">
          <div className="rounded-2xl p-4 border border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl shadow-xl shadow-black/40 max-w-sm">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <div>
                <div className="text-green-400 font-semibold text-sm">Trade Session Started</div>
                <div className="text-slate-300 text-xs mt-1">
                  {sessionStartTime.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    second: '2-digit' 
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
</div>
);
};

export default BinaryTradingBot;